import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useData } from "@/lib/data";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { sizesFromKoli } from "@/components/KoliQty";
import { ModelColorLine, cols, sel, content } from "@/components/ModelColorLine";
import { Plus, X } from "lucide-react";

const pairsOf = (l) => Object.values(l.sizes).reduce((a, b) => a + (parseInt(b) || 0), 0);

const ModelSelect = ({ p, model, codes, onModel }) => (
  <Select value={model} onValueChange={onModel}>
    <SelectTrigger data-testid={`${p}-model`} className={`${sel} h-11 flex-1 sm:max-w-xs font-mono font-bold`}>
      <SelectValue placeholder="Model kodu seçin..." />
    </SelectTrigger>
    <SelectContent className={content}>
      {codes.length === 0 && (
        <div className="px-3 py-2 text-sm text-[#8C7B68]">Stok kartı yok. <Link to="/kart" className="text-[#B45309] underline">Kart oluştur</Link></div>
      )}
      {codes.map((c) => <SelectItem key={c} value={c} data-testid={`${p}-model-option-${c}`}>{c}</SelectItem>)}
    </SelectContent>
  </Select>
);

const NoKoliHint = ({ p }) => (
  <p className="text-xs text-[#B45309]" data-testid={`${p}-no-koli`}>Koli tipi tanımlı değil. <Link to="/koli" className="underline font-semibold">Koli Tipleri</Link> sayfasından ekleyin veya numaraları (▼) elle girin.</p>
);

// One model in the order form: model dropdown, then one compact line per color
export default function OrderModelBlock({ idx, lines, onModel, onUpdate, onUpdateSize, onAddColor, onRemoveLine, onRemoveBlock, usedModels = [], prefix = "order-block", renderExtra }) {
  const { products, koliTypes } = useData();
  const [open, setOpen] = useState({});
  const codes = useMemo(() => [...new Set(products.map((p) => p.model_code))].filter((c) => !usedModels.includes(c)), [products, usedModels]);
  const model = lines[0].model;
  const colors = useMemo(() => products.filter((p) => p.model_code === model), [products, model]);
  const total = lines.reduce((a, l) => a + pairsOf(l), 0);
  const p = `${prefix}-${idx}`;

  const setKoli = (l, typeId, count) => {
    const t = koliTypes.find((k) => k.id === typeId);
    onUpdate(l.id, { koli_type_id: typeId, koli_count: count, sizes: sizesFromKoli(t, parseInt(count) || 0) });
  };

  return (
    <div className="rounded-md border border-[#DCCFB8] bg-[#EFE6D6] p-3" data-testid={p}>
      <div className="flex items-center gap-2 mb-2">
        <ModelSelect p={p} model={model} codes={codes} onModel={onModel} />
        <span className="ml-auto text-xs font-mono text-[#6B5B4A]" data-testid={`${p}-total`}>{total} çift</span>
        {onRemoveBlock && <button type="button" onClick={onRemoveBlock} data-testid={`${p}-remove`} title="Modeli kaldır" className="text-[#8C7B68] hover:text-[#EF4444]"><X className="w-4 h-4" /></button>}
      </div>
      {model && (
        <div className="space-y-1.5 mt-2">
          {koliTypes.length === 0 && <NoKoliHint p={p} />}
          <div className={`hidden sm:grid ${cols} gap-2 px-1 text-[10px] font-mono uppercase text-[#6B5B4A]`}>
            <span>Renk</span><span>Koli tipi</span><span>Koli</span><span className="text-right">Çift</span><span /><span />
          </div>
          {lines.map((l, i) => (
            <ModelColorLine key={l.id} p={p} i={i} l={l} koliTypes={koliTypes}
              opts={colors.filter((c) => c.id === l.product_id || !lines.some((x) => x.product_id === c.id))}
              open={!!open[l.id]} onToggle={() => setOpen((o) => ({ ...o, [l.id]: !o[l.id] }))}
              onUpdate={onUpdate} onUpdateSize={onUpdateSize} setKoli={setKoli}
              onRemove={lines.length > 1 ? () => onRemoveLine(l.id) : null} extra={renderExtra?.(l, i)} />
          ))}
          {lines.length < colors.length && (
            <Button type="button" variant="ghost" onClick={onAddColor} data-testid={`${p}-add-color`}
              className="h-8 px-2 text-[#B45309] hover:bg-[#FFFBF4] hover:text-[#B45309]">
              <Plus className="w-4 h-4 mr-1" /> Renk Ekle
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
